/**
 * Seletor de emojis para inserção em texto
 * 
 * Componente que exibe emojis organizados por categoria em um popover.
 * Permite busca e navegação por categorias via tabs de ícones.
 * Usado no input de mensagens do chat para inserir emojis.
 * 
 * Reutiliza o componente base EmojiGrid para consistência visual.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Smile } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmojiGrid } from '@/components/ui/emoji-grid';

interface EmojiPickerProps {
  /** Callback ao selecionar um emoji */
  onSelect: (emoji: string) => void;
  /** Se o picker está desabilitado */
  disabled?: boolean;
  /** Classe adicional para o botão trigger */
  className?: string;
}

/**
 * EmojiPicker - Seletor de emojis para inserção em texto
 * 
 * Permite seleção múltipla sem fechar o popover, ideal para
 * composição de mensagens com vários emojis.
 */
export function EmojiPicker({
  onSelect,
  disabled = false,
  className,
}: EmojiPickerProps) {
  // Estado controlado do popover
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-9 w-9 text-foreground hover:text-primary', className)}
          disabled={disabled}
          title="Inserir emoji"
        >
          <Smile className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-96 p-0 bg-popover border shadow-lg" 
        side="top" 
        align="start"
        sideOffset={8}
        // data-radix-scroll-lock-ignore: libera eventos de wheel/touch dentro do popover
        // quando ele estiver renderizado dentro de um Radix Dialog (que usa react-remove-scroll
        // e bloqueia scroll fora do DialogContent). Sem isso, o grid de emojis não rola
        // quando o picker é usado nos dialogs de envio de mídia.
        data-radix-scroll-lock-ignore=""
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <EmojiGrid 
          onSelect={onSelect}
          height="360px"
          columns={8}
          showSearch={true}
          showCategories={true}
        />
      </PopoverContent>
    </Popover>
  );
}
